import React, { type FC, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Autocomplete as MuiAutocomplete,
  IconButton,
  InputAdornment,
  ListItem,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Typography,
  useTheme
} from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { mergeSx } from 'merge-sx';
import { useDebounceValue } from 'usehooks-ts';

import SearchIcon from '@/assets/icons/search.svg?react';
import withFormControl from '@/hoc/withFormControl';
import { useDirection } from '@/hooks/useDirection';
import shadows from '@/theme/shadows';
import typography from '@/theme/typography';

import ClearIcon from './assets/clear.svg?react';

interface AutocompleteOption {
  label: string;
  value: string;
  to?: string;
}

interface AutocompleteProps {
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  onChange?: <EventType>(event: EventType) => void;
  options?: AutocompleteOption[];
  sx?: SxProps<Theme>;
  freeSolo?: boolean;
  loadOptions?: (input: string) => Promise<AutocompleteOption[]>;
}

const AutocompleteField: FC<AutocompleteProps> = ({
  id,
  value,
  onChange,
  options: externalOptions,
  loadOptions,
  placeholder,
  sx,
  freeSolo
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState(loadOptions ? [] : externalOptions);
  const { spacing } = useTheme();
  const { applyDirectionalStyles } = useDirection();
  const [isFocused, setIsFocused] = useState(false);
  const isFocusedOrFilled = isFocused || !!inputValue;

  const [debouncedInputValue] = useDebounceValue(inputValue, AUTOCOMPLETE_DEBOUNCE_DELAY);

  const onLoadOptions = useCallback(
    async (newValue: string) => {
      if (!loadOptions || (newValue?.length || 0) < MIN_SEARCH_LENGTH) return;
      const newOptions = await loadOptions(newValue!);
      setOptions(newOptions);
    },
    [loadOptions]
  );

  useEffect(() => {
    if (value === inputValue) return;
    setInputValue(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (loadOptions) return;
    setOptions(externalOptions);
  }, [externalOptions, loadOptions]);

  useEffect(() => {
    onLoadOptions(debouncedInputValue);
  }, [debouncedInputValue, onLoadOptions]);

  const onValueChange = useCallback(
    (_event: SyntheticEvent, newValue: string | AutocompleteOption | null) => {
      const newStringValue = typeof newValue === 'string' ? newValue : newValue?.value;
      onChange?.(newStringValue || '');
    },
    [onChange]
  );

  const onInputChange = useCallback((_event: SyntheticEvent, newValue: string | null) => {
    setInputValue(newValue!);
  }, []);

  const onFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const onBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const onClear = useCallback(() => {
    onChange?.('');
    setInputValue('');
  }, [onChange]);

  return (
    <MuiAutocomplete
      id={id}
      freeSolo={freeSolo ?? true}
      value={inputValue || ''}
      onChange={onValueChange}
      onInputChange={onInputChange}
      options={options!}
      openOnFocus={false}
      open={!!inputValue && isFocused}
      sx={mergeSx<Theme>(
        {
          '&.MuiAutocomplete-root .MuiInputBase-root': {
            pt: 0,
            pb: 0,
            mt: 0.25,
            mb: 0.25,
            bgcolor: 'background.default',
            ...applyDirectionalStyles(1, 0, 'pl', 'pr'),
            '&.Mui-focused': {
              boxShadow: shadows[2]
            },
            ...(!!inputValue && {
              '& fieldset': {
                borderColor: 'primary.1000'
              }
            })
          },
          '&.MuiAutocomplete-root.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiInputBase-root':
            applyDirectionalStyles(1, 0, 'pl', 'pr'),
          '&.MuiAutocomplete-root .MuiInputBase-root .MuiInputBase-input': {
            pl: 0
          }
        },
        sx
      )}
      componentsProps={{
        paper: {
          sx: {
            '&.MuiAutocomplete-paper': {
              bgcolor: 'background.default',
              boxShadow: shadows[1]
            },
            '& .MuiAutocomplete-listbox': {
              p: 0
            }
          }
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            value: inputValue,
            onFocus: onFocus,
            onBlur: onBlur,
            startAdornment: !isFocusedOrFilled && (
              <InputAdornment position="start" sx={{ color: 'grey.700' }}>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isFocusedOrFilled && (
              <InputAdornment position="end" sx={{ color: 'grey.700' }}>
                <IconButton onClick={onClear}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          placeholder={!isFocused ? placeholder : ''}
        />
      )}
      renderOption={(props, option, { inputValue }) => {
        // See: https://github.com/mui/material-ui/issues/42161
        // eslint-disable-next-line react/prop-types
        const { key, sx, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
          key: string;
          to?: string;
          sx: SxProps<Theme>;
        };
        const matches = match(option.label, inputValue, { insideWords: true, findAllOccurrences: true });
        const parts = parse(option.label, matches);

        return (
          <ListItem
            key={key}
            {...optionProps}
            sx={mergeSx<Theme>(sx, {
              '&.MuiAutocomplete-option': {
                p: 0,
                '&:hover': {
                  bgcolor: 'primary.200'
                }
              }
            })}
          >
            <Stack
              component={Link}
              direction="row"
              to={option.to!}
              p={spacing(1.5, 1.25)}
              width="100%"
              sx={{ textDecoration: 'none' }}
            >
              {parts.map((part, index) => (
                <Typography
                  key={index}
                  sx={{
                    ...typography.body2,
                    fontWeight: part.highlight ? typography.fontWeightMedium : typography.fontWeightRegular,
                    lineHeight: 1.5,
                    whiteSpace: 'pre',
                    color: 'text.primary'
                  }}
                >
                  {part.text}
                </Typography>
              ))}
            </Stack>
          </ListItem>
        );
      }}
    />
  );
};

const Autocomplete = withFormControl<Event, string, AutocompleteProps>(AutocompleteField);

export default Autocomplete;

export const AutocompleteWide = ({ sx, ...props }: AutocompleteProps) => (
  <Autocomplete
    {...props}
    sx={mergeSx(
      {
        '& .MuiFormControl-root': { p: 0 },
        '& .MuiInputBase-root': { minWidth: '300px', pt: 0, pb: 0 }
      },
      sx
    )}
  />
);

const AUTOCOMPLETE_DEBOUNCE_DELAY = 100;
const MIN_SEARCH_LENGTH = 2;
